import { Link } from "react-router-dom";
import { Award, Sparkles, Users, Globe2, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const badges = [
  { code: "MWBE", label: "Minority Woman-Owned Business Enterprise" },
  { code: "WOSB", label: "Woman-Owned Small Business" },
  { code: "DBE",  label: "Disadvantaged Business Enterprise" },
  { code: "8(a)", label: "SBA 8(a) — Aligned / Pursuing" },
  { code: "SDB",  label: "Small Disadvantaged Business" },
  { code: "MBE",  label: "Minority Business Enterprise" },
];

const pillars = [
  { icon: Sparkles, k: "Representation", v: "Black, woman, and minority leadership at every operational level." },
  { icon: Users,    k: "Workforce Equity", v: "Hiring, training and advancement programs anchored in the communities we serve." },
  { icon: Globe2,   k: "Economic Impact", v: "Reinvesting contract dollars into the diverse supplier and labor ecosystem." },
];

export default function Diversity() {
  useSEO({
    title: "Supplier Diversity — Black Woman-Owned Business",
    description: "Tandem T Holdings LLC is a Black woman-owned, minority-owned enterprise. MWBE · WOSB · DBE · 8(a) aligned. Founded by Chrissy Thompson.",
    path: "/diversity",
  });
  return (
    <div data-testid="page-diversity">
      {/* HERO — declarative editorial block */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diag-strike -z-10" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-9">
            <Reveal>
              <p className="overline text-white/55">Supplier Diversity · Minority Woman-Owned</p>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="font-serif font-light text-white text-5xl sm:text-6xl md:text-8xl leading-[0.95] tracking-tight mt-8">
                A <em className="italic">Black Woman-Owned</em>{" "}<br />enterprise. By design.
              </h1>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="font-sans font-light text-white/75 text-base md:text-lg mt-10 max-w-2xl leading-relaxed">
                Tandem T Holdings is not "diverse-on-paper." Diversity is the founding architecture of this
                enterprise — the leadership, the labor, the partners, the legacy. We help our customers
                and agencies turn supplier diversity into measurable, mission-aligned outcomes.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.26} className="md:col-span-3">
            <Award size={36} className="text-white" />
            <p className="overline text-white/55 mt-4">Founder-Led · Owner-Operated</p>
          </Reveal>
        </div>
      </section>

      {/* BADGES GRID */}
      <section className="py-24 md:py-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-white/55">Certifications & Designations</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-5xl mt-4 leading-tight">
              Aligned with the frameworks that matter.
            </h2>
            <p className="text-white/60 font-light mt-5 max-w-2xl">
              The following designations represent the certifications, eligibilities and frameworks Tandem T
              Holdings is positioned within — actively held, in active pursuit, or aligned in operating posture.
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10">
            {badges.map((b, i) => (
              <Reveal key={b.code} delay={i * 0.05} className="bg-black">
                <div className="p-8 md:p-12 min-h-[220px] flex flex-col justify-between hover:bg-white/[0.03] transition-colors">
                  <p className="font-serif text-white text-5xl md:text-6xl font-light tracking-tight">{b.code}</p>
                  <div>
                    <div className="w-12 h-px bg-white/40 my-4" />
                    <p className="overline text-white/60 leading-snug">{b.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="text-xs text-white/40 mt-6 max-w-2xl font-light">
              Certification status disclosed upon formal engagement. Generic indicia presented for informational
              context — Tandem T provides primary documentation under NDA or capabilities statement request.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 md:py-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-white/55">Our Diversity Doctrine</p>
            <h2 className="font-serif font-light text-white text-3xl md:text-5xl mt-4 leading-tight">
              Three pillars. Measurable impact.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.k} delay={i * 0.08} className="bg-black">
                  <div className="p-10 min-h-[260px]">
                    <Icon size={22} className="text-white" />
                    <p className="overline text-white/45 mt-8">0{i + 1}</p>
                    <h3 className="font-serif font-light text-white text-3xl mt-3">{p.k}</h3>
                    <p className="text-white/65 font-light leading-relaxed mt-5">{p.v}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <Reveal>
            <h3 className="font-serif font-light text-white text-3xl md:text-5xl max-w-2xl leading-tight">
              Sourcing for diversity goals or set-aside scopes?
            </h3>
          </Reveal>
          <Reveal delay={0.08}>
            <Link
              data-testid="diversity-cta-contact"
              to="/contact?intent=diversity"
              className="group inline-flex items-center gap-3 px-6 py-4 bg-white text-black overline hover:bg-white/85"
            >
              Request Diversity Profile <ArrowUpRight size={16} className="hover-arrow" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
